import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Dialog,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import {
  AccessTime,
  AccountCircle,
  ArrowCircleLeftOutlined,
  CakeOutlined,
  CalendarToday,
  CardMembershipOutlined,
  FilePresentOutlined,
  FilePresentRounded,
  FileUploadRounded,
  LocalHospital,
  LockClockOutlined,
  MedicalServicesOutlined,
  PersonOutline,
  Schedule,
  Timer,
  TimerOff,
  TimerOutlined,
  ToggleOffOutlined,
  ToggleOnOutlined,
} from "@mui/icons-material";

import axios from "axios";
import momentTz from "moment-timezone";
// import { useNavigate } from "react-router-dom";

import { getToken } from "auth/authenticate";

import DeedButtons from "components/common/Elements/DeedButtons";
import ReviewCard from "components/common/ReviewModal/ReviewCard";

import ChatComponent from "../appointmentByAdmin/ChatComponent/ChatComponent";
import useAlert from "pages/appointmentByAdmin/VideoConsultationComponents/useAlert";

import {
  CONSULTATION_STAGE,
  resolvePatientConsultationStage,
} from "./utils/resolvePatientConsultationStage";

import "./joinVideoConsultationInternal.css";

const resolveDisplayTimezone = () =>
  localStorage.getItem("Time-Zone") || momentTz.tz.guess() || "UTC";

const formatUtcTime = (value, timezone) => {
  if (!value) {
    return "Unavailable";
  }

  const parsedValue = momentTz.utc(value);

  if (!parsedValue.isValid()) {
    return "Unavailable";
  }

  return parsedValue.tz(timezone).format("hh:mm A");
};

const formatUtcDate = (value, timezone) => {
  if (!value) {
    return "Unavailable";
  }

  const parsedValue = momentTz.utc(value);

  if (!parsedValue.isValid()) {
    return "Unavailable";
  }

  return parsedValue.tz(timezone).format("DD MMM YYYY");
};

const formatUtcDateTime = (value, timezone) => {
  if (!value) {
    return "Unavailable";
  }

  const parsedValue = momentTz.utc(value);

  if (!parsedValue.isValid()) {
    return "Unavailable";
  }

  return parsedValue.tz(timezone).format("DD MMM YYYY, hh:mm A");
};

const calculateAge = (birthDateValue) => {
  if (!birthDateValue) {
    return "Unknown";
  }

  const birthDate = momentTz(birthDateValue);

  if (!birthDate.isValid()) {
    return "Unknown";
  }

  const now = momentTz();

  const years = now.diff(birthDate, "years");

  const months = now.diff(birthDate.clone().add(years, "years"), "months");

  return `${years} Years, ${months} Months`;
};

const getAttachmentNameFromUrl = (value) => {
  if (typeof value !== "string") {
    return "Attachment";
  }

  const withoutQuery = value.split("?")[0];

  const filename = withoutQuery.substring(withoutQuery.lastIndexOf("/") + 1);

  if (!filename) {
    return "Attachment";
  }

  try {
    return decodeURIComponent(filename);
  } catch (error) {
    return filename;
  }
};

const getStagePresentation = (
  consultationStage,
  timing,
  timezone,
  appointmentStatus,
) => {
  switch (consultationStage) {
    case CONSULTATION_STAGE.SCHEDULED:
      return {
        severity: "info",
        title: "Consultation scheduled",
        message: `The waiting room opens at ${formatUtcDateTime(
          timing?.waitingOpensAt,
          timezone,
        )}.`,
        buttonLabel: "Waiting Room Not Open",
      };

    case CONSULTATION_STAGE.WAITING:
      return {
        severity: "info",
        title: "Waiting room available",
        message: `The waiting room is now available. Your consultation starts at ${formatUtcDateTime(
          timing?.slotAt,
          timezone,
        )}.`,
        buttonLabel: "Go to Waiting Room",
      };

    case CONSULTATION_STAGE.JOINABLE:
      return {
        severity: "success",
        title: "Consultation ready",
        message:
          "Your consultation can now be joined through the waiting room.",
        buttonLabel: "Go to Waiting Room",
      };

    case CONSULTATION_STAGE.HARD_ENDED:
      if (appointmentStatus === "PENDING") {
        return {
          severity: "warning",
          title: "Consultation expired",
          message:
            "The scheduled consultation time has passed, and this appointment has not been marked as completed.",
          buttonLabel: "Consultation Expired",
        };
      }

      if (appointmentStatus === "COMPLETED") {
        return {
          severity: "success",
          title: "Consultation ended",
          message:
            "This consultation has ended and the appointment has been completed.",
          buttonLabel: "Consultation Ended",
        };
      }

      return {
        severity: "warning",
        title: "Consultation ended",
        message: "The consultation access period has ended.",
        buttonLabel: "Consultation Ended",
      };

    default:
      return {
        severity: "warning",
        title: "Consultation unavailable",
        message: "This consultation is currently unavailable.",
        buttonLabel: "Unavailable",
      };
  }
};

const JoinVideoConsultation2 = ({
  appointmentData,
  consultationContext,
  consultationStage,
  refreshConsultationContext,
  onEnterWaitingRoom,
}) => {
  // const navigate = useNavigate();

  const { showAlert, AlertComponent } = useAlert();

  const chatRef = useRef(null);

  const [activeChatView, setActiveChatView] = useState("idle");

  // const [
  //   endConsultationClicked,
  //   setEndConsultationClicked,
  // ] = useState(false);

  const [showReviewModal, setShowReviewModal] = useState(false);

  const [hasReviewed, setHasReviewed] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState([]);

  const [attachments, setAttachments] = useState(
    Array.isArray(appointmentData?.attachments)
      ? appointmentData.attachments
      : [],
  );

  const timezone = resolveDisplayTimezone();

  const timing = consultationContext?.timing;

  const effectiveStage =
    consultationStage || resolvePatientConsultationStage(consultationContext);

  const consultationAvailable =
    consultationContext?.consultationAvailable === true;

  const waitingRoomAccessible = timing?.waitingRoomAccessible === true;

  const joinable = timing?.joinable === true;

  const hardEnded =
    timing?.hardEnded === true ||
    effectiveStage === CONSULTATION_STAGE.HARD_ENDED;

  const appointmentId = appointmentData?.appointmentId;

  const provider = appointmentData?.Doctor;

  const patient = appointmentData?.Patient;

  const providerName =
    provider?.displayName ||
    [provider?.prefix, provider?.name].filter(Boolean).join(" ") ||
    provider?.doctorName ||
    "Unknown health professional";

  const providerProfession =
    provider?.professionType || provider?.profession || "Health professional";

  const providerRegNoAuthority =
    provider?.regNoAuthority || provider?.authority || "Reg. no.";

  const appointmentStatus = String(appointmentData?.status || "").toUpperCase();

  const stagePresentation = useMemo(
    () =>
      getStagePresentation(effectiveStage, timing, timezone, appointmentStatus),
    [effectiveStage, timing, timezone, appointmentStatus],
  );

  const attachmentUrls = useMemo(
    () =>
      attachments.filter(
        (attachment) => typeof attachment === "string" && attachment.trim(),
      ),
    [attachments],
  );

  const isWaitingRoomDisabled =
    !appointmentId ||
    !consultationAvailable ||
    !waitingRoomAccessible ||
    hardEnded ||
    typeof onEnterWaitingRoom !== "function";

  useEffect(() => {
    setAttachments(
      Array.isArray(appointmentData?.attachments)
        ? appointmentData.attachments
        : [],
    );
  }, [appointmentData?.attachments]);

  useEffect(() => {
    if (!appointmentId) {
      return;
    }

    let active = true;

    const loadReviewStatus = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}` +
            `appointment-review/` +
            `${encodeURIComponent(appointmentId)}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          },
        );

        if (!active) {
          return;
        }

        setHasReviewed(Boolean(response?.data?.data?.patient?.reviewId));
      } catch (error) {
        if (active) {
          /*
           * Unknown review state must not be
           * treated as confirmed reviewed.
           */
          setHasReviewed(false);
        }
      }
    };

    loadReviewStatus();

    return () => {
      active = false;
    };
  }, [appointmentId]);

  /*
   * Keep the existing signal behavior until
   * useAgora is audited.
   */
  // useEffect(() => {
  //   if (!endConsultationClicked) {
  //     return;
  //   }

  //   const timerId = window.setTimeout(
  //     () => {
  //       setEndConsultationClicked(false);
  //     },
  //     0,
  //   );

  //   return () => {
  //     window.clearTimeout(timerId);
  //   };
  // }, [endConsultationClicked]);

  const handleToggleChatView = () => {
    setActiveChatView((previous) => (previous === "chat" ? "idle" : "chat"));

    window.setTimeout(() => {
      chatRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);

    setSelectedFiles(files.slice(0, 1));

    /*
     * Allow selecting the same file again.
     */
    event.target.value = "";
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((previous) =>
      previous.filter((_, fileIndex) => fileIndex !== index),
    );
  };

  const handleUploadFiles = async () => {
    if (!selectedFiles.length) {
      showAlert("warning", "alert", "Please select a file to upload");

      return;
    }

    if (!appointmentId) {
      showAlert("error", "alert", "Appointment ID is unavailable");

      return;
    }

    const formData = new FormData();

    formData.append("file", selectedFiles[0]);

    formData.append("appointmentId", appointmentId);

    formData.append("fileType", "Patient Record");

    try {
      const result = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}` +
          "appointment/patient/upload-attachment",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",

            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      const updatedAttachments = result?.data?.data?.attachments;

      if (Array.isArray(updatedAttachments)) {
        setAttachments(updatedAttachments);
      }

      setSelectedFiles([]);

      showAlert("success", "alert", "File uploaded successfully");
    } catch (error) {
      console.error("File upload error:", error);

      showAlert(
        "error",
        "alert",
        error?.response?.data?.message || "Failed to upload file",
      );
    }
  };

  const handleReviewSent = () => {
    setHasReviewed(true);
    setShowReviewModal(false);
  };

  // const handleConsultationComplete =
  //   () => {
  //     setEndConsultationClicked(true);
  //   };

  return (
    <div className="join-video-consultation">
      <Stack spacing={2}>
        <h3>Appointment Details</h3>

        {AlertComponent}

        <Alert severity={stagePresentation.severity}>
          {stagePresentation.message}
        </Alert>

        {/* {!agoraAppId && (
          <Alert severity="error">
            Video consultation configuration is unavailable. Configure
            REACT_APP_AGORA_APP_ID.
          </Alert>
        )} */}

        <Dialog
          open={showReviewModal}
          onClose={() => {}}
          disableEscapeKeyDown
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 1,
            },
          }}
        >
          <ReviewCard
            appointment={appointmentData}
            onReviewSent={handleReviewSent}
          />
        </Dialog>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={3}
            className="appointment-details"
            sx={{
              width: "100%",
              maxWidth: 980,
              mx: "auto",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Paper>
              <Box
                sx={{
                  p: {
                    xs: 2,
                    sm: 3,
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "#1976d2",
                    borderBottom: "2px solid #1976d2",
                    pb: 1,
                    mb: 1,
                  }}
                >
                  Appointment Information
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 3,
                  }}
                >
                  ID: {appointmentId || "Unavailable"}
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        bgcolor: "#f5f5f5",
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "#2196f3",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <AccountCircle />
                        Patient Information
                      </Typography>

                      <Stack spacing={2}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Box
                            sx={{
                              minWidth: 90,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <PersonOutline color="primary" fontSize="small" />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Name
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600}>
                            {patient?.name || "Unknown Patient"}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Box
                            sx={{
                              minWidth: 90,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <CakeOutlined color="primary" fontSize="small" />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Age
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600}>
                            {calculateAge(patient?.dob)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        bgcolor: "#f5f5f5",
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                        height: "100%",
                      }}
                    >
                      {/* <Typography variant="subtitle" >Health Service Provider</Typography> */}
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "#2196f3",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <LocalHospital />

                        {providerProfession}
                      </Typography>

                      <Stack spacing={2}>
                        {/* PROVIDER NAME */}
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              minWidth: 72,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <MedicalServicesOutlined
                              color="primary"
                              fontSize="small"
                            />

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Name
                            </Typography>
                          </Box>

                          <Typography variant="body2" fontWeight={600}>
                            {providerName}
                          </Typography>
                        </Box>

                        {/* REGISTRATION */}
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              minWidth: 72,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <CardMembershipOutlined
                              color="primary"
                              fontSize="small"
                            />

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              textAlign="center"
                            >
                              {providerRegNoAuthority}
                            </Typography>
                          </Box>

                          <Typography variant="body2" fontWeight={600}>
                            {provider?.regNo || "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        bgcolor: "#e3f2fd",
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid #90caf9",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "#1976d2",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <Schedule />
                        Appointment Schedule
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 70,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <CalendarToday color="primary" fontSize="small" />

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Date
                              </Typography>
                            </Box>

                            <Typography variant="body2" fontWeight={600}>
                              {formatUtcDate(
                                timing?.slotAt || appointmentData?.slotTime,
                                timezone,
                              )}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 90,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <AccessTime color="primary" fontSize="small" />

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                textAlign="center"
                              >
                                Start Time
                              </Typography>
                            </Box>

                            <Typography variant="body2" fontWeight={600}>
                              {formatUtcTime(
                                timing?.slotAt || appointmentData?.slotTime,
                                timezone,
                              )}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <TimerOutlined
                            color="primary"
                            fontSize="small"
                          />

                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Maximum time
                            </Typography>

                            <Typography
                              variant="body2"
                              fontWeight={600}
                            >
                              {timing
                                ?.maximumMinutes
                                ? `${timing.maximumMinutes} minutes`
                                : "Unavailable"}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid> */}

                        <Grid item xs={12} sm={6} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 95,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <TimerOff color="error" fontSize="small" />

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                textAlign="center"
                              >
                                Expiration
                              </Typography>
                            </Box>

                            <Typography variant="body2" fontWeight={600}>
                              {formatUtcDateTime(timing?.hardEndsAt, timezone)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          mt: 2,
                        }}
                      >
                        Display timezone: {timezone}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        bgcolor: "#fff",
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <FilePresentRounded />
                        Attachments
                      </Typography>

                      {attachmentUrls.length ? (
                        <Stack spacing={1}>
                          {attachmentUrls.map((url) => (
                            <Button
                              key={url}
                              variant="outlined"
                              startIcon={<FilePresentOutlined />}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                justifyContent: "flex-start",
                                textTransform: "none",
                              }}
                            >
                              {getAttachmentNameFromUrl(url)}
                            </Button>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No attachments available. Upload your previous medical
                          reports if needed.
                        </Typography>
                      )}

                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="text"
                          component="label"
                          sx={{
                            bgcolor: "#64684f",
                            color: "white",
                            textTransform: "none",
                            "&:hover": {
                              bgcolor: "#393b2e",
                            },
                          }}
                          startIcon={<FileUploadRounded />}
                        >
                          Select Files
                          <input
                            type="file"
                            hidden
                            onChange={handleFileChange}
                          />
                        </Button>

                        <Stack spacing={1} mt={1}>
                          {selectedFiles.map((file, index) => (
                            <Stack
                              key={`${file.name}-${file.size}`}
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <FilePresentOutlined fontSize="small" />

                              <Typography variant="body2">
                                {file.name}
                              </Typography>

                              <Button
                                size="small"
                                color="error"
                                onClick={() => handleRemoveFile(index)}
                              >
                                Remove
                              </Button>
                            </Stack>
                          ))}
                        </Stack>

                        {selectedFiles.length > 0 && (
                          <Button
                            variant="contained"
                            component="label"
                            sx={{
                              bgcolor: "#3C3D37 !important",
                              textTransform: "none",
                              "&:hover": {
                                bgcolor: "#23241e !important",
                              },
                            }}
                            onClick={handleUploadFiles}
                          >
                            Submit Attachment
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                className="button-group"
                sx={{
                  width: "100%",
                  p: 2,
                  display: "flex",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: 1.5,
                }}
              >
                <DeedButtons
                  variant="contained"
                  onClick={onEnterWaitingRoom}
                  disabled={isWaitingRoomDisabled}
                  sx={{
                    width: "auto",
                    minWidth: {
                      xs: 170,
                      sm: 180,
                    },
                    height: {
                      xs: 40,
                      sm: 44,
                    },
                    px: {
                      xs: 2,
                      sm: 2.5,
                    },
                    py: 0.75,
                    fontSize: {
                      xs: "0.9rem",
                      sm: "0.95rem",
                    },
                    borderRadius: "20px !important",
                    whiteSpace: "nowrap",
                  }}
                >
                  {hardEnded
                    ? appointmentStatus === "PENDING"
                      ? "Consultation Expired"
                      : "Consultation Ended"
                    : waitingRoomAccessible
                      ? "Go to Waiting Room"
                      : "Waiting Room Not Open"}
                </DeedButtons>

                <Button
                  variant={activeChatView === "chat" ? "contained" : "outlined"}
                  color="secondary"
                  onClick={handleToggleChatView}
                  sx={{
                    width: "auto",
                    minWidth: {
                      xs: 170,
                      sm: 180,
                    },
                    height: {
                      xs: 40,
                      sm: 44,
                    },
                    px: {
                      xs: 2,
                      sm: 2.5,
                    },
                    py: 0.75,
                    fontSize: {
                      xs: "0.9rem",
                      sm: "0.95rem",
                    },
                    borderRadius: "20px !important",
                    whiteSpace: "nowrap",
                  }}
                  startIcon={
                    activeChatView === "chat" ? (
                      <ToggleOnOutlined />
                    ) : (
                      <ToggleOffOutlined />
                    )
                  }
                >
                  {activeChatView === "chat"
                    ? "Hide Report View"
                    : "Show Report View"}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>

        <div className="dynamic-views">
          <div className="chat-container" ref={chatRef}>
            {activeChatView === "chat" && (
              <ChatComponent aptId={appointmentId} />
            )}
          </div>
        </div>
      </Stack>
    </div>
  );
};

export { JoinVideoConsultation2 };
